import { supabase, Account, AuditLog, Transaction } from '../lib/supabase';

type ExportAccount = Account & {
  flats?: Array<{ flat_number: string; physical_address?: string; participation_quota?: number | null }>;
  account_owners?: Array<{ owner_id: string }>;
  owners?: Array<{ full_name: string; email?: string; telephone?: string }>;
};

type ExportTransaction = Transaction & {
  accounts?: { account_number: string };
};

export class ExportService {
  private bodyCorporateId: string;

  constructor(bodyCorporateId: string) {
    this.bodyCorporateId = bodyCorporateId;
  }

  buildCsvContent(headers: string[], rows: Array<Array<string | number | null | undefined>>) {
    return [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell ?? ''}"`).join(','))
    ].join('\n');
  }

  async exportAccountsToCSV() {
    try {
      if (!this.bodyCorporateId) {
        throw new Error('No body corporate configured for export');
      }

      const { data: accounts, error: accountsError } = await supabase
        .from('accounts')
        .select('*')
        .eq('body_corporate_id', this.bodyCorporateId);

      if (accountsError) throw accountsError;

      const headers = [
        'Account Number',
        'Flat Number',
        'Participation Quota',
        'Owner Name',
        'Email',
        'Phone',
        'Status',
        'Balance',
        'Opened On',
      ];

      const rows = await Promise.all((accounts || []).map(async (account: Account) => {
        const balanceResult = await supabase.rpc('get_account_balance', { p_account_id: account.id });
        const balance = Number(balanceResult?.data || 0);

        const { data: flat } = await supabase
          .from('flats')
          .select('flat_number, physical_address, participation_quota')
          .eq('id', account.flat_id)
          .maybeSingle();

        const { data: ownerLink } = await supabase
          .from('account_owners')
          .select('owner_id')
          .eq('account_id', account.id)
          .eq('is_primary', true)
          .maybeSingle();

        let ownerName = '';
        let ownerEmail = '';
        let ownerTelephone = '';

        if (ownerLink?.owner_id) {
          const { data: owner } = await supabase
            .from('owners')
            .select('full_name, email, telephone')
            .eq('id', ownerLink.owner_id)
            .maybeSingle();

          ownerName = owner?.full_name || '';
          ownerEmail = owner?.email || '';
          ownerTelephone = owner?.telephone || '';
        }

        return [
          account.account_number,
          flat?.flat_number || '',
          flat?.participation_quota ?? '',
          ownerName,
          ownerEmail,
          ownerTelephone,
          account.status,
          balance.toFixed(2),
          account.opened_on,
        ];
      }));

      return this.buildCsvContent(headers, rows);
    } catch (error) {
      console.error('Error exporting accounts:', error);
      throw error;
    }
  }

  async exportTransactionsToCSV(accountId?: string, fromDate?: string, toDate?: string) {
    try {
      if (!this.bodyCorporateId) {
        throw new Error('No body corporate configured for export');
      }

      let query = supabase
        .from('transactions')
        .select('*')
        .eq('body_corporate_id', this.bodyCorporateId)
        .eq('status', 'posted');

      if (accountId) {
        query = query.eq('account_id', accountId);
      }
      if (fromDate) {
        query = query.gte('transaction_date', fromDate);
      }
      if (toDate) {
        query = query.lte('transaction_date', toDate);
      }

      const { data, error } = await query.order('transaction_date', { ascending: true });

      if (error) throw error;

      const headers = [
        'Account Number',
        'Date',
        'Type',
        'Description',
        'Reference',
        'Debit',
        'Credit',
        'Notes',
      ];

      const rows = await Promise.all((data || []).map(async (t: Transaction) => {
        const { data: accountData } = await supabase
          .from('accounts')
          .select('account_number')
          .eq('id', t.account_id)
          .maybeSingle();

        return [
          accountData?.account_number || '',
          t.transaction_date,
          t.transaction_type,
          t.description,
          t.reference_number || '',
          Number(t.debit_amount || 0).toFixed(2),
          Number(t.credit_amount || 0).toFixed(2),
          t.notes || '',
        ];
      }));

      return this.buildCsvContent(headers, rows);
    } catch (error) {
      console.error('Error exporting transactions:', error);
      throw error;
    }
  }

  async exportAuditTrailToCSV(fromDate?: string, toDate?: string) {
    try {
      if (!this.bodyCorporateId) {
        throw new Error('No body corporate configured for export');
      }

      let query = supabase
        .from('audit_logs')
        .select('*')
        .eq('body_corporate_id', this.bodyCorporateId);

      if (fromDate) {
        query = query.gte('created_at', fromDate);
      }
      if (toDate) {
        query = query.lte('created_at', toDate);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      const headers = [
        'Timestamp',
        'User',
        'Action',
        'Entity',
        'Entity ID',
        'Details',
      ];

      const rows = await Promise.all((data || []).map(async (log: AuditLog) => {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', log.user_id)
          .maybeSingle();

        return [
          log.created_at,
          profileData?.full_name || log.user_id || 'Unknown',
          log.action,
          log.entity_type,
          log.entity_id,
          JSON.stringify({
            old: log.old_data,
            new: log.new_data,
          }),
        ];
      }));

      return this.buildCsvContent(headers, rows);
    } catch (error) {
      const permissionError = (error as any)?.code === '42501'
        || (error as any)?.message?.toLowerCase().includes('permission denied for table audit_logs');

      if (permissionError) {
        const headers = [
          'Timestamp',
          'User',
          'Action',
          'Entity',
          'Entity ID',
          'Details',
        ];
        console.warn('Audit trail export skipped because the authenticated Supabase role lacks SELECT on public.audit_logs. Run GRANT SELECT ON public.audit_logs TO authenticated;');
        return this.buildCsvContent(headers, []);
      }

      console.error('Error exporting audit trail:', error);
      throw error;
    }
  }

  downloadCSV(csvContent: string, filename: string) {
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
